import React, { useEffect, useState } from 'react';
import s from './s.module.less';
import { Button, Form, Modal, Select, Switch, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { EditModuleEnum } from 'types/practiceFront';
import { STATE } from 'constants/meta';
import useAllMedications from 'hooks/useAllMedications';

interface IMedicationData {
    state: string;
    nonPrescribableMedications: string[];
    prescribableMedications: string[];
    syncAllState: boolean;
}

interface IMedicationsPrescribedProps {
    field: EditModuleEnum;
    medications?: IMedicationData[];
    appliedStates?: string[];
    onSubmit?: (data: {
        medications: IMedicationData[];
    }, field: EditModuleEnum, callback?: () => void) => void;
}

const MedicationsPrescribed: React.FC<IMedicationsPrescribedProps> = ({
    field = EditModuleEnum.GENERIC_MEDICATIONS_PRESCRIBED,
    medications = [],
    appliedStates = [],
    onSubmit,
}) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [selectedState, setSelectedState] = useState<string>('');
    const [syncAcrossAllStates, setSyncAcrossAllStates] = useState(false);
    // 添加本地状态来保存药物数据的修改
    const [localMedicationData, setLocalMedicationData] = useState<IMedicationData[]>([]);

    // 使用 hook 获取所有药物数据
    const { medications: allMedications, loading: medicationsLoading, error: medicationsError } = useAllMedications();

    // 初始化本地药物数据
    useEffect(() => {
        // 确保所有药物数据都是数组而不是null
        const sanitizedMedications = medications.map((item) => ({
            ...item,
            prescribableMedications: Array.isArray(item.prescribableMedications) ? item.prescribableMedications : [],
            nonPrescribableMedications: Array.isArray(item.nonPrescribableMedications) ? item.nonPrescribableMedications : [],
        }));
        setLocalMedicationData(sanitizedMedications);
    }, [medications]);

    // 将药物字符串数组转换为 Select 选项格式
    const medicationOptions = allMedications.map((medication) => ({
        label: medication,
        value: medication,
    }));

    // 处理药物获取错误
    useEffect(() => {
        if (medicationsError) {
            console.error('Failed to load medications:', medicationsError);
        }
    }, [medicationsError]);

    // 将完整州名转换为州缩写
    const getStateAbbreviation = (fullStateName: string): string => {
        const stateInfo = STATE.find((state) => state.label === fullStateName);
        return stateInfo && typeof stateInfo.value === 'string' ? stateInfo.value : fullStateName;
    };

    // 查找 syncAllState 为 true 的州，若表单当前syncAcrossAllStates为true，则直接返回当前选中州
    const findsyncAllStateState = () => {
        if (syncAcrossAllStates) return selectedState;
        if (!localMedicationData || localMedicationData.length === 0) return null;
        const syncState = localMedicationData.find((item) => item.syncAllState === true);
        if (!syncState) return null;
        const stateInfo = STATE.find((state) => state.value === syncState.state);
        return stateInfo ? stateInfo.label : syncState.state;
    };

    // 清理数据：确保只有一个州可以有 syncAllState: true
    const cleanupSyncStates = (medicationData: IMedicationData[]) => {
        if (!medicationData || medicationData.length === 0) return medicationData;

        const syncStates = medicationData.filter((item) => item.syncAllState === true);
        if (syncStates.length > 1) {
            // 如果有多个同步状态，只保留第一个，其他的设为 false
            medicationData.forEach((item, index) => {
                if (item.syncAllState === true && index > 0) {
                    item.syncAllState = false;
                }
            });
        }
        return medicationData;
    };

    // 初始化选中的州
    useEffect(() => {
        if (appliedStates.length > 0 && !selectedState) {
            // 清理数据，确保只有一个州可以有同步状态
            if (localMedicationData && localMedicationData.length > 0) {
                cleanupSyncStates(localMedicationData);
            }

            // 优先选择有 syncAllState: true 的州
            const syncState = findsyncAllStateState();
            if (syncState) {
                setSelectedState(syncState);
                console.log('Initial state set to sync state:', syncState);
            } else {
                setSelectedState(appliedStates[0]);
                console.log('Initial state set to first applied state:', appliedStates[0]);
            }
        }
    }, [appliedStates, selectedState, localMedicationData]);

    // 获取当前选中州的药物数据（使用州缩写查找）
    const selectedStateAbbr = getStateAbbreviation(selectedState);

    // 聚合所有州的药物数据
    const getAllPrescribableMedications = () => {
        if (!localMedicationData || localMedicationData.length === 0) return [];

        const allPrescribable = localMedicationData
            .filter((item) => item.prescribableMedications && Array.isArray(item.prescribableMedications) && item.prescribableMedications.length > 0)
            .flatMap((item) => item.prescribableMedications || []);

        // 去重
        return Array.from(new Set(allPrescribable));
    };

    const getAllNonPrescribableMedications = () => {
        if (!localMedicationData || localMedicationData.length === 0) return [];

        const allNonPrescribable = localMedicationData
            .filter((item) => item.nonPrescribableMedications && Array.isArray(item.nonPrescribableMedications) && item.nonPrescribableMedications.length > 0)
            .flatMap((item) => item.nonPrescribableMedications || []);

        // 去重
        return Array.from(new Set(allNonPrescribable));
    };

    const allPrescribableMedications = getAllPrescribableMedications();
    const allNonPrescribableMedications = getAllNonPrescribableMedications();

    // 检查是否有有效数据
    const hasValidData = allPrescribableMedications.length > 0 || allNonPrescribableMedications.length > 0;

    const isEditMode = hasValidData;

    useEffect(() => {
        if (modalVisible) {
            // 弹窗打开时，优先检查是否有 syncAllState 为 true 的州
            const syncState = findsyncAllStateState();
            let targetState = selectedState;

            if (syncState) {
                // 如果存在同步状态的州，强制切换到该州
                targetState = syncState;
                if (selectedState !== syncState) {
                    setSelectedState(syncState);
                    console.log('Modal opened - auto-selected sync state:', syncState);
                }
            } else if (!selectedState && appliedStates.length > 0) {
                // 如果没有选中任何州，选择第一个应用的州
                targetState = appliedStates[0];
                setSelectedState(appliedStates[0]);
            }

            // 初始化表单数据，使用目标州的数据
            const stateAbbr = getStateAbbreviation(targetState);
            const stateData = localMedicationData && localMedicationData.length > 0
                ? localMedicationData.find((item) => item.state === stateAbbr)
                : null;

            form.setFieldsValue({
                prescribableMedications: (stateData?.prescribableMedications && Array.isArray(stateData.prescribableMedications)) ? stateData.prescribableMedications : [],
                nonPrescribableMedications: (stateData?.nonPrescribableMedications && Array.isArray(stateData.nonPrescribableMedications)) ? stateData.nonPrescribableMedications : [],
                syncAcrossAllStates: stateData?.syncAllState || false,
            });
            setSyncAcrossAllStates(stateData?.syncAllState || false);
        }
    }, [modalVisible, localMedicationData, form, appliedStates]);

    // 兼容Antd4，使用onValuesChange
    const handleFormValuesChange = (changedValues: any) => {
        if ('syncAcrossAllStates' in changedValues) {
            setSyncAcrossAllStates(!!changedValues.syncAcrossAllStates);
            // 如果开启同步，保持当前选中州
            if (changedValues.syncAcrossAllStates) {
                // 开启同步时，不需要额外操作，州标签会被自动禁用
                console.log('Sync enabled - state tabs will be disabled');
            } else {
                // 关闭同步时，需要将所有州的 syncAllState 都设为 false
                setLocalMedicationData((prevData) => {
                    return prevData.map((item) => ({
                        ...item,
                        syncAllState: false,
                    }));
                });
                console.log('Sync disabled - all states syncAllState set to false');
            }
        }
    };

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            // 在提交之前，先保存当前表单的数据
            const finalMedicationData = [...localMedicationData];

            if (selectedState) {
                const currentStateAbbr = getStateAbbreviation(selectedState);
                const existingIndex = finalMedicationData.findIndex((item) => item.state === currentStateAbbr);
                const currentData = {
                    state: currentStateAbbr,
                    prescribableMedications: (values.prescribableMedications || []).filter((item: string) => !!item),
                    nonPrescribableMedications: (values.nonPrescribableMedications || []).filter((item: string) => !!item),
                    syncAllState: values.syncAcrossAllStates || false,
                };

                if (existingIndex >= 0) {
                    finalMedicationData[existingIndex] = currentData;
                } else {
                    finalMedicationData.push(currentData);
                }
            }

            const currentsyncAllState = values.syncAcrossAllStates || false;

            // 如果当前开启了 syncAcrossAllStates，只提交当前州的数据
            if (currentsyncAllState) {
                const newData = {
                    state: selectedStateAbbr,
                    prescribableMedications: (values.prescribableMedications || []).filter((item: string) => !!item),
                    nonPrescribableMedications: (values.nonPrescribableMedications || []).filter((item: string) => !!item),
                    syncAllState: true,
                };

                onSubmit?.({
                    medications: [newData], // 只提交当前州的数据
                }, field, () => {
                    setModalVisible(false);
                });
                return;
            }

            // 检查是否存在 syncAllState=true 的州
            const hasSyncState = finalMedicationData.some((item) => item.syncAllState === true);
            if (hasSyncState) {
                // 如果存在 syncAllState=true 的州，只提交该州的数据
                const syncState = finalMedicationData.find((item) => item.syncAllState === true);
                if (syncState) {
                    onSubmit?.({
                        medications: [syncState], // 只提交同步州的数据
                    }, field, () => {
                        setModalVisible(false);
                    });
                    return;
                }
            }

            // 原有的逻辑：更新当前州的数据 - 使用深拷贝避免引用共享
            const updatedMedicationData = JSON.parse(JSON.stringify(finalMedicationData));
            const existingIndex = updatedMedicationData.findIndex((item: IMedicationData) => item.state === selectedStateAbbr);

            const newData = {
                state: selectedStateAbbr, // 使用州缩写保存数据
                prescribableMedications: (values.prescribableMedications || []).filter((item: string) => !!item),
                nonPrescribableMedications: (values.nonPrescribableMedications || []).filter((item: string) => !!item),
                syncAllState: false,
            };

            if (existingIndex >= 0) {
                updatedMedicationData[existingIndex] = newData;
            } else {
                updatedMedicationData.push(newData);
            }

            // 确保所有州的 syncAllState 都是 false（当前用户没有开启同步）
            updatedMedicationData.forEach((item: IMedicationData) => {
                if (item.state !== selectedStateAbbr) {
                    item.syncAllState = false;
                }
            });

            onSubmit?.({
                medications: updatedMedicationData,
            }, field, () => {
                setModalVisible(false);
            });
        });
    };

    const handleStateSelect = (state: string) => {
        // 优先检查表单中的 syncAcrossAllStates 开关状态
        // 如果表单开关打开，且点击的不是当前选中州，则阻止切换
        if (syncAcrossAllStates && state !== selectedState) {
            return;
        }

        // 在切换州之前，先保存当前表单的数据到当前州
        if (modalVisible && selectedState) {
            const currentValues = form.getFieldsValue();
            const currentStateAbbr = getStateAbbreviation(selectedState);

            // 更新当前州的数据到本地药物数据中
            setLocalMedicationData((prevData) => {
                const newData = [...prevData];
                const existingIndex = newData.findIndex((item) => item.state === currentStateAbbr);
                const updatedData = {
                    state: currentStateAbbr,
                    prescribableMedications: (currentValues.prescribableMedications || []).filter((item: string) => !!item),
                    nonPrescribableMedications: (currentValues.nonPrescribableMedications || []).filter((item: string) => !!item),
                    syncAllState: currentValues.syncAcrossAllStates || false,
                };

                if (existingIndex >= 0) {
                    newData[existingIndex] = updatedData;
                } else {
                    newData.push(updatedData);
                }
                return newData;
            });
        }

        setSelectedState(state);

        // 切换州时，更新表单数据
        if (modalVisible) {
            const stateAbbr = getStateAbbreviation(state);
            const stateData = localMedicationData && localMedicationData.length > 0
                ? localMedicationData.find((item) => item.state === stateAbbr)
                : null;

            form.setFieldsValue({
                prescribableMedications: (stateData?.prescribableMedications && Array.isArray(stateData.prescribableMedications)) ? stateData.prescribableMedications : [],
                nonPrescribableMedications: (stateData?.nonPrescribableMedications && Array.isArray(stateData.nonPrescribableMedications)) ? stateData.nonPrescribableMedications : [],
                syncAcrossAllStates: stateData?.syncAllState || false,
            });
            // 同步本地状态
            setSyncAcrossAllStates(stateData?.syncAllState || false);
        }
    };

    return (
        <div className={s.wrap}>
            <Modal
                open={modalVisible}
                title={isEditMode ? 'Edit Medications prescribed' : 'Add Medications prescribed'}
                onCancel={() => setModalVisible(false)}
                okText="Save changes"
                onOk={handleSubmit}
                destroyOnClose
            >
                <div className={s.modal}>
                    <Form form={form} layout="vertical" onValuesChange={handleFormValuesChange}>
                        {/* Applied states 标签选择器 */}
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14 }}>Applied states</div>
                            <div className={s.statesTabs}>
                                {appliedStates.map((state) => {
                                    // 优先检查表单中的 syncAcrossAllStates 开关状态
                                    // 如果表单开关打开，其他州都禁用，只有当前选中州可点
                                    const isDisabled = syncAcrossAllStates && state !== selectedState;

                                    return (
                                        <div
                                            key={state}
                                            className={`${s.stateTab} ${selectedState === state ? s.stateTabActive : ''} ${isDisabled ? s.stateTabDisabled : ''}`}
                                            onClick={() => !isDisabled && handleStateSelect(state)}
                                            style={{
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                opacity: isDisabled ? 0.5 : 1,
                                            }}
                                        >
                                            {state}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={s.syncRow}>
                            <div className={s.syncLabel}>
                                <span className={s.syncText}>Sync across all states</span>
                                <Tooltip title="When enabled, the same medication settings will apply to all states. Any customizations made for individual states will be replaced.">
                                    <QuestionCircleOutlined className={s.questionIcon} />
                                </Tooltip>
                            </div>
                            <Form.Item
                                name="syncAcrossAllStates"
                                valuePropName="checked"
                                className={s.switchItem}
                            >
                                <Switch size="small" />
                            </Form.Item>
                        </div>
                        <Form.Item
                            label="Medications provider can prescribe"
                            name="prescribableMedications"
                        >
                            <Select
                                mode="tags"
                                options={medicationOptions}
                                loading={medicationsLoading}
                                placeholder="Select or type medications..."
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                        <Form.Item
                            label="Medications provider can't prescribe"
                            name="nonPrescribableMedications"
                        >
                            <Select
                                mode="tags"
                                options={medicationOptions}
                                loading={medicationsLoading}
                                placeholder="Select or type medications..."
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Form>
                </div>
            </Modal>
            <div className={s.header}>
                <div className={s.title}>Medications prescribed</div>
                <Button onClick={() => setModalVisible(true)}>{isEditMode ? 'Edit' : 'Add'}</Button>
            </div>
            {!hasValidData ? (
                <div className={s.none}>Nothing here yet</div>
            ) : (
                <div className={s.content}>
                    <div className={s.item}>
                        <div className={s.appliedStatesRow}>
                            <div className={s.itemTitle}>Applied states</div>
                        </div>
                        <div className={s.itemValue}>
                            {appliedStates.length > 0
                                ? appliedStates.map((item) => (
                                    <div key={item} className={s.tag}>
                                        {item}
                                    </div>
                                ))
                                : 'Not provided'}
                        </div>
                    </div>
                    <div className={s.item}>
                        <div className={s.itemTitle}>
                            Medications provider may can prescribe
                        </div>
                        <div className={s.itemValue}>
                            {allPrescribableMedications.length > 0
                                ? allPrescribableMedications.map((item) => (
                                    <div key={item} className={s.tag}>
                                        {item}
                                    </div>
                                ))
                                : 'Not provided'}
                        </div>
                    </div>
                    <div className={s.item}>
                        <div className={s.itemTitle}>
                            Medications provider can&apos;t prescribe
                        </div>
                        <div className={s.itemValue}>
                            {allNonPrescribableMedications.length > 0
                                ? allNonPrescribableMedications.map((item) => (
                                    <div key={item} className={s.tag}>
                                        {item}
                                    </div>
                                ))
                                : 'Not provided'}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MedicationsPrescribed;
